import { DestroyRef, Service, effect, inject, untracked } from '@angular/core'
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { filter, take } from 'rxjs'
import { NavigationStart, Router } from '@angular/router'
import type { ComponentRef } from '@angular/core'
import { destroyModal } from '../../../shared/functions/destroy'
import { DynamicRefService } from '../../../shared/services/dynamic-ref-service/dynamic-ref.service'
import { NotificationViewComponent } from '../../../features/notifications/views/notification-view/notification-view.component'
import { WorkspaceSwitchService } from '../workspace-switch-service/workspace-switch.service'

/** Opens the notifications list as a sheet above the current mobile screen. */
@Service()
export class NotificationSheetService {
  private readonly dynamicRefService = inject(DynamicRefService)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly workspaceSwitchService = inject(WorkspaceSwitchService)
  private notificationSheetRef: ComponentRef<NotificationViewComponent> | null = null

  constructor() {
    this.closeOnNavigation()
    effect(() => this.closeOnWorkspaceSwitch())
  }

  /** Closes the old sheet when navigation leaves its screen. */
  private closeOnNavigation(): void {
    this.router.events
      .pipe(
        // Android updates the modal query parameter while staying on the same screen.
        filter(event => event instanceof NavigationStart && event.url.split('?')[0] !== this.router.url.split('?')[0]),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.notificationSheetRef) {
          this.close(this.notificationSheetRef)
        }
      })
  }

  /** Closes Notifications when the workspace context is replaced. */
  private closeOnWorkspaceSwitch(): void {
    if (this.workspaceSwitchService.isSwitching()) {
      untracked(() => {
        if (this.notificationSheetRef) {
          this.close(this.notificationSheetRef)
        }
      })
    }
  }

  /** Opens the notifications sheet once. */
  show(): void {
    if (this.notificationSheetRef) {
      return
    }

    const [componentRef, component] = this.dynamicRefService.create(NotificationViewComponent)

    this.notificationSheetRef = componentRef
    componentRef.setInput('sheet', true)

    outputToObservable(component.closed)
      .pipe(take(1))
      .subscribe(() => this.close(componentRef))
  }

  /**
   * Destroys the currently open notifications sheet.
   * @param {ComponentRef<NotificationViewComponent>} componentRef Sheet to close.
   */
  private close(componentRef: ComponentRef<NotificationViewComponent>): void {
    if (this.notificationSheetRef !== componentRef) {
      return
    }

    componentRef.instance.saveScrollPosition()
    this.notificationSheetRef = null
    destroyModal(componentRef)
  }
}
